import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, firstValueFrom, from, of, throwError, EMPTY } from 'rxjs';
import { switchMap, catchError, map, tap, expand, reduce } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { CreateVehicleDto, VehicleDto, VehicleResponse, VehicleSidebarSummaryDto, VehicleByLicensePlateResponse } from 'src/app/models/vehicle.model';
import { AuthService } from 'src/app/auth/auth.service';
import { AnalyticsService } from 'src/app/analytics/analytics.service';
import { AnalyticsEvent } from 'src/app/analytics/models/analytics-event';

@Injectable({
  providedIn: 'root'
})
export class VehiclesService {
  private readonly apiUrl = `${environment.cafler_backoffice_back_url}/vehicles`;

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService,
    private readonly analytics: AnalyticsService
  ) { }

  private getAuthHeaders(token: string): HttpHeaders {
    return new HttpHeaders({
      Authorization: `Bearer ${token}`
    });
  }

  private handleError(operation: string, err: unknown) {
    console.error(`VehiclesService.${operation} failed:`, err);
    return throwError(() => err);
  }

  getAll(page = 1, pageSize = 10, query = '', centerId?: number): Observable<VehicleResponse> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) return throwError(() => new Error('No token available'));
        let params = new HttpParams()
          .set('page', String(page))
          .set('size', String(pageSize));
        if (query && query.trim().length > 0) {
          params = params.set('search', query.trim());
        }
        if (centerId !== undefined && centerId !== null) {
          params = params.set('centerId', centerId.toString());
        }
        return this.http.get<VehicleResponse>(this.apiUrl, {
          headers: this.getAuthHeaders(token),
          params
        }).pipe(
          catchError(err => this.handleError('getAll', err))
        );
      })
    );
  }

  getById(id: number): Observable<VehicleDto> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) return throwError(() => new Error('No token available'));
        const endpoint = `${this.apiUrl}/${encodeURIComponent(String(id))}`;
        return this.http.get<VehicleDto>(endpoint, {
          headers: this.getAuthHeaders(token)
        }).pipe(
          catchError(err => this.handleError('getById', err))
        );
      })
    );
  }

  getByClientId(clientId: number): Observable<VehicleDto[]> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) return throwError(() => new Error('No token available'));
        const pageSize = 50;
        const fetchPage = (page: number) => {
          const params = new HttpParams()
            .set('clientId', String(clientId))
            .set('page', String(page))
            .set('size', String(pageSize));

          return this.http.get<VehicleResponse>(this.apiUrl, {
            headers: this.getAuthHeaders(token),
            params
          });
        };

        return fetchPage(1).pipe(
          expand((response: VehicleResponse) => {
            const currentPage = response.metadata?.currentPage ?? 1;
            const hasNextPage = Boolean(response.metadata?.hasNext);
            return hasNextPage ? fetchPage(currentPage + 1) : EMPTY;
          }),
          map((response: VehicleResponse) => response.items ?? []),
          reduce((allVehicles: VehicleDto[], pageVehicles: VehicleDto[]) => allVehicles.concat(pageVehicles), []),
          catchError(err => this.handleError('getByClientId', err))
        );
      })
    );
  }

  create(vehicle: CreateVehicleDto): Observable<VehicleDto> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) return throwError(() => new Error('No token available'));
        return this.http.post<VehicleDto>(this.apiUrl, vehicle, {
          headers: this.getAuthHeaders(token)
        }).pipe(
          tap((resp: VehicleDto) => {
            this.analytics.track(AnalyticsEvent.VehicleCreateSuccess, {
              ...this.analytics.globalProps(),
              vehicle_id: resp.id,
              plate_provided: !!vehicle.licensePlate,
              vin_provided: !!vehicle.vin,
              linked_customer: !!vehicle.clientId
            });
          }),
          catchError((err: HttpErrorResponse) => {
            const errorCode = err.error?.statusCode ?? 'UNKNOWN_ERROR';
            const errorMessage = err.error?.message ?? err.statusText ?? 'Unknown error';

            this.analytics.track(AnalyticsEvent.VehicleCreateFail, {
              ...this.analytics.globalProps(),
              error_code: errorCode,
              error_message: errorMessage,
              http_status: err.status,
              plate_provided: !!vehicle.licensePlate,
              linked_customer: !!vehicle.clientId,
            });
            this.handleError('create', err)

            return throwError(() => err);
          })
        );
      })
    );
  }

  update(id: number, vehicle: Partial<CreateVehicleDto>): Observable<VehicleDto> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) return throwError(() => new Error('No token available'));
        const endpoint = `${this.apiUrl}/${encodeURIComponent(String(id))}`;
        return this.http.put<VehicleDto>(endpoint, vehicle, {
          headers: this.getAuthHeaders(token)
        }).pipe(



          catchError(err => this.handleError('update', err))
        );
      })
    );
  }

  delete(id: number): Observable<void> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) return throwError(() => new Error('No token available'));
        const endpoint = `${this.apiUrl}/${encodeURIComponent(String(id))}`;
        return this.http.delete<void>(endpoint, {
          headers: this.getAuthHeaders(token)
        }).pipe(
          catchError(err => this.handleError('delete', err))
        );
      })
    );
  }

  getVehicleHistory(vehicleId: number, options?: { summary?: boolean }): Observable<VehicleSidebarSummaryDto> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) return throwError(() => new Error('No token available'));
        let params = new HttpParams();
        if (options?.summary !== undefined) {
          params = params.set('summary', String(Boolean(options.summary)));
        }
        const endpoint = `${this.apiUrl}/${encodeURIComponent(String(vehicleId))}/details`;
        return this.http.get<VehicleSidebarSummaryDto>(endpoint, {
          headers: this.getAuthHeaders(token),
          params
        }).pipe(
          catchError(err => this.handleError('getVehicleHistory', err))
        );
      })
    );
  }

  getSidebarSummary(vehicleId: number): Observable<VehicleSidebarSummaryDto> {
    return this.getVehicleHistory(vehicleId, { summary: true });
  }

  getCreateVehicleDto_From_vehicle(vehicle: VehicleDto): CreateVehicleDto {
    return {
      brand: vehicle.brand,
      vehicleType: vehicle.vehicleType,
      fuelType: vehicle.fuelType,
      model: vehicle.model,
      vin: vehicle.vin ?? undefined,
      licensePlate: vehicle.licensePlate ?? undefined,
      firstRegistrationDate: vehicle.firstRegistrationDate ?? null,
      manufacturingDate: vehicle.manufacturingDate ?? null,
      clientId: vehicle.clientId,
      numberOfDoors: vehicle.numberOfDoors ?? undefined,
      numberOfSeats: vehicle.numberOfSeats ?? undefined,
      transmissionType: vehicle.transmissionType ?? undefined,
      driveType: vehicle.driveType ?? undefined,
      maximumTare: vehicle.maximumTare ?? undefined,
      enginePower: vehicle.enginePower ?? undefined,
    }
  }

  private toVehicleDisplay(vehicle: VehicleDto): string {
    const primaryIdentifier = vehicle.licensePlate || vehicle.vin || '-';
    return `${primaryIdentifier} - ${vehicle.brand} ${vehicle.model}`;
  }

  async searchVehiclesByClientId(clientId: number): Promise<VehicleDto[]> {
    if (!clientId) return [];

    try {
      const vehicles = await firstValueFrom(
        this.getByClientId(clientId).pipe(catchError(() => of([] as VehicleDto[])))
      );

      return vehicles.map((vehicle: VehicleDto) => ({
        ...vehicle,
        display: this.toVehicleDisplay(vehicle),
      }));

    } catch (error) {
      console.error('Error al buscar vehículos del cliente:', error);
      return [];
    }
  }

  getByLicensePlate(licensePlate: string): Observable<VehicleByLicensePlateResponse> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) {
          throw new Error('No token available');
        }
        return this.http.get<VehicleByLicensePlateResponse>(`${this.apiUrl}/by-license-plate/${licensePlate}`, {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        });
      })
    );
  }

  getRecentDocumentImages(vehicleId: number): Observable<{ [docType: string]: string }> {
    return from(this.authService.getAccessToken()).pipe(
      switchMap((token) => {
        if (!token) return throwError(() => new Error('No token available'));
        const endpoint = `${this.apiUrl}/${encodeURIComponent(String(vehicleId))}/recent-documents`;
        return this.http.get<{ [docType: string]: string }>(endpoint, {
          headers: this.getAuthHeaders(token)
        }).pipe(
          catchError(err => this.handleError('getRecentDocumentImages', err))
        );
      })
    );
  }


  async searchVehiclesSearcher(query: string, centerId?: number): Promise<VehicleDto[]> {
    const response = await firstValueFrom(this.getAll(1, 20, query, centerId));
    const vehicles = response.items ?? [];
    const normalizedQuery = query.trim().toLowerCase();

    const filteredVehicles = normalizedQuery
      ? vehicles.filter((vehicle: VehicleDto) =>
        vehicle.brand?.toLowerCase().includes(normalizedQuery) ||
        vehicle.model?.toLowerCase().includes(normalizedQuery) ||
        vehicle.licensePlate?.toLowerCase().includes(normalizedQuery)
      )
      : vehicles;

    return filteredVehicles.map((vehicle: VehicleDto) => ({
      ...vehicle,
      display: this.toVehicleDisplay(vehicle)
    }));
  }
}
